// src/mocks/demoSetup.js
// Executed once before React mounts when REACT_APP_DEMO_MODE=true.
// Responsibilities:
//   1. Seed localStorage so AuthProvider finds a valid pre-authenticated session.
//   2. Register an axios request interceptor that replaces the network adapter
//      for all known API endpoints, returning mock data instead.

import axios from 'axios';
import { DEMO_TOKEN, DEMO_REFRESH_TOKEN, DEMO_USER } from './data/user.demo';
import { resolveHandler } from './handlers/index';

// ── 1. Seed auth state ────────────────────────────────────────────────────────
localStorage.setItem('authAccessToken', DEMO_TOKEN);
localStorage.setItem('authRefreshToken', DEMO_REFRESH_TOKEN);
localStorage.setItem('authToken', DEMO_TOKEN); // legacy key used by some services
localStorage.setItem('authUser', JSON.stringify(DEMO_USER));

// ── 2. Axios request interceptor ─────────────────────────────────────────────
axios.interceptors.request.use(
    (config) => {
        // External dolar API — intercept before route table lookup
        if (config.url && config.url.includes('dolarapi.com')) {
            config.adapter = async (adapterConfig) => ({
                data: { compra: 1340, venta: 1390, casa: 'oficial', nombre: 'Oficial', moneda: 'USD', fechaActualizacion: '2026-04-20T10:00:00.000Z' },
                status: 200,
                statusText: 'OK',
                headers: { 'content-type': 'application/json' },
                config: adapterConfig,
                request: {},
            });
            return config;
        }

        const handler = resolveHandler(config);

        if (handler) {
            // Replace the real network adapter with a function that calls the handler
            config.adapter = async (adapterConfig) => {
                // Simulate realistic network latency (150–250 ms)
                await new Promise((r) => setTimeout(r, 150 + Math.random() * 100));

                let responseData;
                try {
                    responseData = await handler(adapterConfig);
                } catch (err) {
                    // Surface handler errors as 500 responses
                    const error = new Error(err.message || 'Demo handler error');
                    error.response = {
                        status: 500,
                        statusText: 'Internal Server Error',
                        data: { success: false, message: err.message },
                        headers: { 'content-type': 'application/json' },
                        config: adapterConfig,
                    };
                    return Promise.reject(error);
                }

                return {
                    data: responseData,
                    status: 200,
                    statusText: 'OK',
                    headers: { 'content-type': 'application/json' },
                    config: adapterConfig,
                    request: {},
                };
            };
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// eslint-disable-next-line no-console
console.log(
    '%c[DEMO MODE] Active — all API calls return hardcoded mock data. No backend required.',
    'background:#ff6b00;color:#fff;padding:4px 8px;border-radius:4px;font-weight:bold;'
);
